import { Box, Link, Typography, useMediaQuery } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import LandscapeIcon from '@evaneos/b2b-ui/icons/LandscapeOutlined';

import { parseTipiAttachmentName } from '../../../../../resources/discussion/operations';
import { MessageAttachment } from '../../../../../resources/discussion/types';

type TipiCardProps = {
    attachment: MessageAttachment;
    onTipiLinkClick: () => void;
};

export const TipiCard = ({ attachment, onTipiLinkClick }: TipiCardProps) => {
    const supportsHover = useMediaQuery('(hover: hover)');
    const { formatMessage, formatDate } = useIntl();
    const { date, label } = parseTipiAttachmentName(attachment.name);

    return (
        <Box
            component="li"
            data-testid="attachment-tipi-link"
            data-spec-id="attachment-tipi-link"
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: 1,
                columnGap: 2,
                p: 2,
                backgroundColor: 'background.variant',
                ...(supportsHover && {
                    '&:hover [data-tipi-hover-link], &:focus-within [data-tipi-hover-link]': {
                        opacity: 1,
                    },
                }),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flex: '1 1 auto',
                    minWidth: 0,
                }}
            >
                <Box
                    width={40}
                    height={40}
                    sx={{
                        backgroundColor: 'background.main',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: 1,
                        flexShrink: 0,
                    }}
                >
                    <LandscapeIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {label}
                    </Typography>
                    {date && (
                        <Typography variant="body2" color="text.secondary">
                            {formatMessage(
                                { id: 'discussion.attachments_list.created_on_date' },
                                {
                                    date: formatDate(date, {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                    }),
                                },
                            )}
                        </Typography>
                    )}
                </Box>
            </Box>
            <Typography
                data-tipi-hover-link
                variant="body2"
                sx={{
                    flexShrink: 0,
                    opacity: supportsHover ? 0 : 1,
                    transition: 'opacity 150ms',
                }}
            >
                <Link
                    href={attachment.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onTipiLinkClick}
                    sx={{ fontWeight: 700 }}
                >
                    {formatMessage({ id: 'discussion.attachments_list.see_in_tipi_link' })}
                </Link>
            </Typography>
        </Box>
    );
};
